import { Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { changeAppLocale } from "@/i18n";
import {
  LOCALE_LABELS,
  SUPPORTED_LOCALES,
  type AppLocale,
} from "@/i18n/locale";
import { cn } from "@/lib/utils";

type LanguageSwitcherProps = {
  variant?: "icon" | "compact" | "full";
  className?: string;
};

export const LanguageSwitcher = ({
  variant = "compact",
  className,
}: LanguageSwitcherProps) => {
  const { i18n, t } = useTranslation();
  const currentLocale = i18n.language as AppLocale;

  const handleSelect = (locale: AppLocale) => {
    if (locale !== currentLocale) {
      changeAppLocale(locale);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size={variant === "icon" ? "icon" : "sm"}
          className={cn(
            variant === "full" ? "gap-2" : variant === "compact" ? "gap-1.5" : "",
            className,
          )}
          aria-label={t("common.language")}
        >
          <Globe className="h-4 w-4 shrink-0" />
          {variant !== "icon" ? (
            <span className="max-w-[7rem] truncate text-sm">
              {LOCALE_LABELS[currentLocale] ?? LOCALE_LABELS.en}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuLabel>{t("common.language")}</DropdownMenuLabel>
        {SUPPORTED_LOCALES.map((locale) => (
          <DropdownMenuItem
            key={locale}
            onClick={() => handleSelect(locale)}
            className={cn(currentLocale === locale && "bg-accent")}
          >
            {LOCALE_LABELS[locale]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
